import { TypedDocumentNode, gql } from '@apollo/client';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import crest from '@sorare/core/src/assets/user/crest.png';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { ClubShield } from '@sorare/core/src/components/user/ClubShield';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { MY_CLUB_BY_SPORT } from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { isType } from '@sorare/core/src/lib/gql';

import { Banner } from 'components/user/Banner';

import { UserNetworkInfo_publicUserInfoInterface } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  position: relative;
`;

type Props = { user: UserNetworkInfo_publicUserInfoInterface };

export const UserNetworkInfo = ({ user }: Props) => {
  const { sport } = useSportContext();

  const profile = {
    [Sport.FOOTBALL]: user.footballUserProfile,
    [Sport.NBA]: user.nbaUserProfile,
    [Sport.BASEBALL]: user.baseballUserProfile,
  }[sport];

  const shieldUrl = isType(profile, 'FootballUserSportProfile')
    ? profile.clubBadge?.pictureUrl
    : profile?.shieldUrl;

  const bannerUrl = isType(profile, 'FootballUserSportProfile')
    ? profile.background?.pictureUrl
    : profile?.bannerUrl;

  const bannerColor = isType(profile, 'FootballUserSportProfile')
    ? null
    : profile?.bannerColor;

  return (
    <>
      <Banner bannerUrl={bannerUrl} bannerColor={bannerColor} />
      <Root>
        <Horizontal>
          <ActiveUserAvatar
            user={user}
            variant="medium"
            placeholderUrl={crest}
          />
          <Vertical gap={0}>
            <Link
              to={generatePath(MY_CLUB_BY_SPORT[sport], {
                slug: user.slug,
              })}
            >
              <BodyM bold color="var(--c-white)">
                <UserName user={user} />
              </BodyM>
            </Link>
            {!!shieldUrl && !!bannerUrl && (
              <Horizontal gap={0.5}>
                <ClubShield
                  size={2}
                  clubName={profile?.clubName}
                  shieldUrl={shieldUrl}
                />
                <LabelS as="span" color="var(--c-nd-600-opaque)" bold>
                  {profile?.clubName}
                </LabelS>
              </Horizontal>
            )}
          </Vertical>
        </Horizontal>
      </Root>
    </>
  );
};

UserNetworkInfo.fragments = {
  user: gql`
    fragment UserNetworkInfo_publicUserInfoInterface on PublicUserInfoInterface {
      slug
      footballUserProfile {
        id
        clubName
        clubBadge {
          id
          pictureUrl
        }
        background {
          id
          pictureUrl
        }
      }
      nbaUserProfile {
        id
        clubName
        shieldUrl
        bannerUrl
        bannerColor
      }
      baseballUserProfile {
        id
        clubName
        shieldUrl
        bannerUrl
        bannerColor
      }
      ...ActiveUserAvatar_user
      ...UserName_publicUserInfoInterface
    }
    ${ActiveUserAvatar.fragments.user}
    ${UserName.fragments.user}
  ` as TypedDocumentNode<UserNetworkInfo_publicUserInfoInterface>,
};
